import { Link } from "react-router-dom";
import { SiteLayout, LightSection, CtaStrip } from "@/components/site/SiteLayout";
import { ArrowRight } from "@phosphor-icons/react";

const GMARI_BARS = [
  ["Evidence Completeness", 64, "#B45309"], ["Country Coverage", 38, "#B91C1C"],
  ["Evidence Reusability", 98, "#047857"], ["Submission Readiness", 72, "#047857"],
];

const ESSENTIALS = [
  ["What is MAI Matrix™?", "MarksMan Healthcare's proprietary, AI-powered Market Access Intelligence Operating System — used by our consultants to power every global market access engagement. It is never sold or licensed as standalone software."],
  ["Who is it for?", "Global Market Access, HEOR, Medical Affairs, Pricing and leadership teams taking pharmaceutical or MedTech products into multiple markets."],
  ["What problem does it solve?", "Global launches stall on evidence that doesn't travel: each market asks different questions, and gaps surface after submissions are already planned. We surface them while your team can still act."],
  ["Why is it different?", "Conventional consulting is slow and manual. AI tools lack judgment. We combine both: AI-scale analysis across every market and evidence asset, validated and interpreted by senior market access consultants."],
  ["What will my leadership team receive?", "A clear verdict on your global readiness, a prioritized evidence and localization roadmap, a defensible submission sequence — presented and defended in person."],
];

const STATS = [
  ["16+", "markets analysed country-by-country, never on average"],
  ["1", "readiness verdict — transparent, traceable, board-ready"],
  ["0", "raw AI outputs delivered — every finding consultant-validated"],
  ["100%", "of scores fully explainable — every weight and threshold on the table"],
];

const BEFORE = ["Evidence scattered across teams, vendors and markets", "Each market planned in isolation — the same evidence rebuilt again and again", "Payer questions arrive as surprises at submission", "Readiness is a feeling, not a governed score", "Evidence spend without a prioritized business case"];
const AFTER = ["One connected picture of evidence, markets and requirements", "Evidence designed once, deployed across markets deliberately", "Requirements and precedents anticipated before submission", "A transparent readiness index leadership can govern by", "A sequenced, €-prioritized evidence investment roadmap"];

export default function SiteHome() {
  return (
    <SiteLayout>
      <header className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(148,163,184,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.06) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="overline-label text-emerald-400">MarksMan Healthcare · HEOR & Market Access Consulting</p>
            <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-5">
              AI-enabled, Expert-led Global Market Access
            </h1>
            <p className="text-slate-400 text-base md:text-lg leading-relaxed mt-6 max-w-xl">
              MAI Matrix™ is MarksMan Healthcare's proprietary Market Access Intelligence Operating System. Our consultants use it to answer the question every launch team asks — <span className="text-white font-semibold">"Are we market access ready?"</span> — market by market, before submissions are on the line.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-9">
              <Link to="/request-demo" data-testid="hero-demo-btn"
                className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-6 py-3.5 flex items-center gap-2">
                Request a Demo <ArrowRight size={15} weight="bold" />
              </Link>
              <Link to="/our-approach" data-testid="hero-approach-link" className="text-sm font-semibold text-slate-300 hover:text-white border border-white/15 px-6 py-3.5">See Our Approach</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div data-testid="hero-gmari-card" className="bg-slate-900 border border-white/10 p-7">
              <p className="overline-label text-slate-500">Sample Executive Dashboard · demo asset</p>
              <p className="font-heading font-extrabold text-white leading-none mt-3" style={{ fontSize: "64px" }}>65<span className="text-xl text-slate-600">/100</span></p>
              <span className="inline-block mt-2 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 text-white" style={{ background: "#1D4ED8" }}>Global Readiness · Advanced</span>
              <div className="mt-6 space-y-3">
                {GMARI_BARS.map(([label, v, color]) => (
                  <div key={label}>
                    <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                      <span className="uppercase tracking-wider">{label}</span>
                      <span className="font-mono-data font-bold text-slate-300">{v}</span>
                    </div>
                    <div className="h-1.5 bg-white/5"><div className="h-full" style={{ width: `${v}%`, background: color }} /></div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 mt-5 leading-relaxed">Illustrative. Every dimension, weight and threshold is fully explained in every engagement — no black-box scores.</p>
            </div>
          </div>
        </div>
      </header>

      <LightSection overline="The essentials, in one minute" title="What you need to know before anything else.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {ESSENTIALS.map(([q, a]) => (
            <div key={q} className="bg-white border border-slate-200 p-6">
              <p className="font-heading font-bold text-slate-900 text-sm">{q}</p>
              <p className="text-sm text-slate-500 leading-relaxed mt-2">{a}</p>
            </div>
          ))}
          <div className="bg-slate-900 p-6 flex flex-col justify-between">
            <p className="font-heading font-bold text-white text-sm">When is the right time to talk?</p>
            <p className="text-sm text-slate-400 leading-relaxed mt-2">Earlier than you think — evidence decisions made years before launch determine what you can defend at submission.</p>
            <Link to="/who-we-serve" className="text-emerald-400 text-xs font-semibold mt-4 inline-flex items-center gap-1.5">See if this is you <ArrowRight size={12} weight="bold" /></Link>
          </div>
        </div>
      </LightSection>

      <LightSection alt overline="Why teams work with us" title="AI-scale analysis. Consultant judgment. One accountable answer.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
          {STATS.map(([n, label]) => (
            <div key={label} className="bg-white p-6">
              <p className="font-heading font-extrabold text-slate-900 text-4xl">{n}</p>
              <p className="text-xs text-slate-500 leading-relaxed mt-2">{label}</p>
            </div>
          ))}
        </div>
      </LightSection>

      <LightSection alt overline="One operating system · seven capabilities" title="MAI Matrix™ — the platform behind every engagement.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div data-testid="capability-knowledge-hub" className="md:col-span-3 bg-slate-900 p-7 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <p className="overline-label text-emerald-400">Flagship capability</p>
              <p className="font-heading font-extrabold text-white text-2xl tracking-tight mt-2">MAI Knowledge Hub™</p>
              <p className="text-sm text-slate-400 leading-relaxed mt-3">
                Where organisational knowledge lives. Every affiliate question — "Germany requests subgroup analyses", "Japan asks about local applicability" — is classified by AI, checked against every previously approved answer, grounded in your evidence base, drafted for human review, and stored as reusable institutional memory. The platform gets smarter with every approved response.
              </p>
            </div>
            <ul className="space-y-2">
              {["Ask anything — AI retrieval across your whole evidence base", "Incoming query workflow with medical review & versioning", "\u201CAnswered before\u201D detection — stop recreating responses", "Learning analytics: recurring questions → GVD update signals"].map((t) => (
                <li key={t} className="text-xs text-slate-300 flex gap-2"><span className="text-emerald-400">✓</span>{t}</li>
              ))}
            </ul>
          </div>
          {[
            ["Strategy Intelligence", "Strategic market prioritization — where to launch, in what order, and why."],
            ["Country Intelligence", "Global market registry with structural profiles, comparisons and similarity engines."],
            ["Evidence Reusability", "Design evidence once, deploy it deliberately across every market."],
            ["Submission Planning", "Wave-based submission sequencing grounded in requirement-level gaps."],
            ["Launch Intelligence", "A governed launch roadmap tied to a transparent readiness index."],
            ["AI Copilot", "A conversational strategist grounded in your live knowledge graph."],
          ].map(([title, desc]) => (
            <div key={title} className="bg-white border border-slate-200 p-6">
              <p className="font-heading font-bold text-slate-900 text-sm">{title}</p>
              <p className="text-sm text-slate-500 leading-relaxed mt-2">{desc}</p>
            </div>
          ))}
        </div>
      </LightSection>

      <LightSection overline="The difference" title="Before and after MAI Matrix™">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white border border-slate-200 p-6">
            <p className="overline-label text-red-700">Without MAI Matrix</p>
            <ul className="mt-4 space-y-2.5">{BEFORE.map((t) => <li key={t} className="text-sm text-slate-500 flex gap-2.5"><span className="text-red-300">✕</span>{t}</li>)}</ul>
          </div>
          <div className="bg-slate-900 p-6">
            <p className="overline-label text-emerald-400">With MAI Matrix</p>
            <ul className="mt-4 space-y-2.5">{AFTER.map((t) => <li key={t} className="text-sm text-slate-300 flex gap-2.5"><span className="text-emerald-400">✓</span>{t}</li>)}</ul>
          </div>
        </div>
      </LightSection>

      <CtaStrip text="Know where your access strategy will stall — before your submissions do." />
    </SiteLayout>
  );
}
